import type { FreightRate } from "@/types";
import { formatDate, formatRupees } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography, shadows } from "@/styles/tokens";

export type FreightStatus = "Active" | "Expiring Soon" | "Expired";

const EXPIRING_WINDOW_DAYS = 7;

export function statusOf(rate: FreightRate, today = new Date()): FreightStatus {
  if (!rate.active) return "Expired";
  if (!rate.validTo) return "Active";
  const to = new Date(rate.validTo + "T00:00:00");
  if (to < today) return "Expired";
  const diffMs = to.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays <= EXPIRING_WINDOW_DAYS) return "Expiring Soon";
  return "Active";
}

// ⚠️ `currency` IS AN ISO CODE OFF res.currency, NOT A TWO-MEMBER UNION.
// It used to be "USD" | "INR", and the dollar sign below was therefore
// exhaustive. It no longer is: these rows come from the database, this
// deployment has adopted five currencies, and a sixth is a row away. So an
// unrecognised code PREFIXES ITSELF rather than borrowing "$" -- a EUR rate
// rendered as "$ 1,200.00" is a wrong number, not a cosmetic slip. An empty
// code prints the bare figure rather than asserting a currency nobody
// recorded.
export function formatMoney(value: number, currency: FreightRate["currency"]): string {
  if (currency === "INR") return formatRupees(value);
  const n = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  if (currency === "USD") return `$ ${n}`;
  return currency ? `${currency} ${n}` : n;
}

// A value the backend holds but this build does not recognise, or does not
// hold at all. Rendered as an em dash rather than guessed -- the same rule
// /catalog follows for an unclassified brand.
function Dash() {
  return <span style={{ color: semanticColors.textPlaceholder }}>—</span>;
}

type ColDef = { header: string; width: number; align: "left" | "right" };
const COLS: ColDef[] = [
  { header: "Origin",       width: 120, align: "left"  },
  { header: "Destination",  width: 170, align: "left"  },
  { header: "Type",         width: 130, align: "left"  },
  { header: "Mode",         width:  76, align: "left"  },
  { header: "Basis",        width: 120, align: "left"  },
  { header: "Rate",         width: 130, align: "right" },
  // Widened (app review P2-9): the two right-aligned headers touched.
  { header: "Min Charge",   width: 124, align: "right" },
  { header: "Transit Days", width: 124, align: "right" },
  { header: "Carrier",      width: 150, align: "left"  },
  { header: "Valid To",     width: 130, align: "left"  },
  { header: "Status",       width: 130, align: "left"  },
];

function TypePill({ type, muted }: { type: FreightRate["type"]; muted: boolean }) {
  const intl = type === "International";
  const fg = muted ? semanticColors.textPlaceholder : (intl ? semanticColors.accentTextOnTint : colors.gray600);
  const bg = muted ? semanticColors.hoverBg : (intl ? colors.indigo50 : semanticColors.hoverBg);
  const bd = muted ? semanticColors.border : (intl ? colors.indigo100 : semanticColors.border);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
      fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
      color: fg, backgroundColor: bg, border: `1px solid ${bd}`,
    }}>{type}</span>
  );
}

function StatusPill({ status }: { status: FreightStatus }) {
  const scheme = {
    "Active":        { fg: colors.green600, bg: colors.green50, bd: colors.green100 },
    "Expiring Soon": { fg: colors.amber600, bg: colors.amber50, bd: colors.amber200 },
    "Expired":       { fg: semanticColors.textMuted, bg: semanticColors.hoverBg, bd: semanticColors.border },
  }[status];
  return (
    <span
      data-testid={`freight-status-${status.replace(/\s+/g, "-").toLowerCase()}`}
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: scheme.fg, backgroundColor: scheme.bg, border: `1px solid ${scheme.bd}`,
      }}
    >
      {status}
    </span>
  );
}

export function FreightRatesTable({
  rows,
  selectedId,
  onRowClick,
}: {
  rows: FreightRate[];
  selectedId?: string;
  onRowClick: (r: FreightRate) => void;
}) {
  return (
    <div
      data-testid="freight-rates-table"
      style={{
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, overflow: "auto",
      }}
    >
      <div style={{ minWidth: COLS.reduce((s, c) => s + c.width, 0) }}>
        {/* Header */}
        <div style={{
          display: "flex", height: heights.control,
          backgroundColor: semanticColors.panelBg, borderBottom: `1px solid ${semanticColors.border}`,
        }}>
          {COLS.map((c) => (
            <div key={c.header} style={{
              width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
              display: "flex", alignItems: "center",
              justifyContent: c.align === "right" ? "flex-end" : "flex-start",
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
              textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
              whiteSpace: "nowrap",
            }}>{c.header}</div>
          ))}
        </div>

        {/* Body */}
        <div>
          {rows.map((r) => {
            const status = statusOf(r);
            const expired = status === "Expired";
            const textColor = expired ? semanticColors.textPlaceholder : semanticColors.textPrimary;
            const mutedColor = expired ? semanticColors.textPlaceholder : semanticColors.textMuted;
            const isSelected = r.id === selectedId;
            return (
              <div
                key={r.id}
                data-testid={`freight-row-${r.id}`}
                data-status={status}
                className="material-row"
                onClick={() => onRowClick(r)}
                style={{
                  display: "flex", height: heights.row,
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: COLS[0].width, minWidth: COLS[0].width, padding: `0 ${spacing[12]}px`,
                  display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                  color: textColor, fontWeight: typography.fontWeight.medium,
                  boxShadow: expired ? `${shadows.alertBarShape} ${colors.amber600}` : undefined,
                }}>{r.origin}</div>
                <div style={{ width: COLS[1].width, minWidth: COLS[1].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm, color: textColor }}>
                  {r.destination}
                </div>
                <div style={{ width: COLS[2].width, minWidth: COLS[2].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  {r.type ? <TypePill type={r.type} muted={expired} /> : <Dash />}
                </div>
                <div style={{ width: COLS[3].width, minWidth: COLS[3].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm, color: textColor }}>
                  {r.mode ?? <Dash />}
                </div>
                <div style={{ width: COLS[4].width, minWidth: COLS[4].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm, color: mutedColor }}>
                  {r.basis ?? <Dash />}
                </div>
                <div style={{ width: COLS[5].width, minWidth: COLS[5].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor, fontWeight: typography.fontWeight.medium,
                              fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {formatMoney(r.rate, r.currency)}
                </div>
                <div style={{ width: COLS[6].width, minWidth: COLS[6].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: mutedColor,
                              fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {r.minCharge != null ? formatMoney(r.minCharge, r.currency) : "—"}
                </div>
                <div style={{ width: COLS[7].width, minWidth: COLS[7].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor, fontVariantNumeric: "tabular-nums" }}>
                  {r.transitDays}
                </div>
                <div style={{ width: COLS[8].width, minWidth: COLS[8].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: textColor, whiteSpace: "nowrap",
                              overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.carrier}
                </div>
                <div style={{ width: COLS[9].width, minWidth: COLS[9].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: r.validTo ? textColor : semanticColors.textPlaceholder,
                              fontVariantNumeric: "tabular-nums" }}>
                  {r.validTo ? formatDate(r.validTo) : "—"}
                </div>
                <div style={{ width: COLS[10].width, minWidth: COLS[10].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  <StatusPill status={status} />
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div
              data-testid="freight-empty"
              style={{
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              No rates match the current filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
